// src/pages/admin/Page_Leads/CampaignRecipientsDrawer.js
//
// Quien hay en una campana y en que punto esta cada uno.
//
// El panel de campanas solo daba el agregado ("376 pendientes"), que no sirve
// para trabajar: hay que poder ver A QUIEN se le ha escrito, a quien le toca,
// y quien ha fallado o se ha saltado y por que.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Drawer,
  Table,
  Tag,
  Input,
  Segmented,
  Typography,
  Tooltip,
  Button,
  Modal,
  Space,
  Alert,
  message,
} from "antd";
import {
  ReloadOutlined,
  SearchOutlined,
  DownloadOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../../../hooks/useAuth";
import { promotionsApi } from "../../../api/promotionsApi";

const { Text } = Typography;

const ESTADO = {
  pending: { color: "default", label: "Pendiente" },
  sending: { color: "processing", label: "Enviando" },
  sent: { color: "green", label: "Enviado" },
  skipped: { color: "gold", label: "Saltado" },
  failed: { color: "red", label: "Fallido" },
};

const TIPO_DOMINIO = {
  personal: { color: "default", label: "Personal" },
  centro: { color: "blue", label: "Centro" },
  universidad: { color: "purple", label: "Universidad" },
  administracion: { color: "cyan", label: "Administracion" },
};

// Una celda de CSV: comillas dobles siempre y escapadas dentro, que en los
// nombres de centro hay comas a puñados ("IES Ramon y Cajal, Zaragoza").
function celda(v) {
  return `"${String(v ?? "").replace(/"/g, '""')}"`;
}

export function CampaignRecipientsDrawer({ campaign, open, onClose }) {
  const { accessToken } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState("todos");
  const [busqueda, setBusqueda] = useState("");
  // Pegado de resultados del validador externo de buzones.
  const [suprimiendo, setSuprimiendo] = useState(false);
  const [modalSupresion, setModalSupresion] = useState(false);
  const [pegado, setPegado] = useState("");

  const load = useCallback(async () => {
    if (!accessToken || !campaign?._id) return;
    setLoading(true);
    try {
      const res = await promotionsApi.getCampaign(accessToken, campaign._id);
      setItems(res?.data?.recipients || []);
    } catch (err) {
      message.error(`Error cargando destinatarios: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [accessToken, campaign]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const filtros = useMemo(() => {
    const n = (s) => items.filter((r) => r.status === s).length;
    return [
      { label: `Todos (${items.length})`, value: "todos" },
      { label: `Pendientes (${n("pending")})`, value: "pending" },
      { label: `Enviados (${n("sent")})`, value: "sent" },
      { label: `Saltados (${n("skipped")})`, value: "skipped" },
      { label: `Fallidos (${n("failed")})`, value: "failed" },
    ];
  }, [items]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return items.filter((r) => {
      if (filtro !== "todos" && r.status !== filtro) return false;
      if (!q) return true;
      return (
        r.email.toLowerCase().includes(q) ||
        (r.name || "").toLowerCase().includes(q) ||
        (r.promo_code || "").toLowerCase().includes(q)
      );
    });
  }, [items, filtro, busqueda]);

  // Exporta lo que se este viendo. Por defecto el filtro esta en "todos", pero
  // para mandar al validador interesa filtrar Pendientes primero: no tiene
  // sentido pagar por verificar a quien ya se le escribio.
  const exportarCsv = useCallback(() => {
    if (visibles.length === 0) {
      message.info("No hay destinatarios que exportar con este filtro.");
      return;
    }
    const cabecera = ["email", "nombre", "cargo", "dominio", "estado"];
    const filas = visibles.map((r) =>
      [r.email, r.name, r.cargo, r.dominio, r.status].map(celda).join(","),
    );
    // BOM para que Excel abra los acentos bien.
    const csv = `﻿${cabecera.join(",")}\n${filas.join("\n")}`;
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `campana-${(campaign?.name || "lista")
      .replace(/[^a-z0-9]+/gi, "-")
      .toLowerCase()}-${filtro}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success(`${visibles.length} destinatarios exportados.`);
  }, [visibles, campaign, filtro]);

  // Acepta lo que devuelva el validador tal cual: una columna de emails, un
  // CSV entero, separados por comas o por lineas. Se queda con lo que tenga
  // forma de direccion y descarta el resto en vez de dar error.
  const emailsPegados = useMemo(() => {
    const encontrados = String(pegado).match(
      /[^\s@,;"']+@[a-z0-9.-]+\.[a-z]{2,}/gi,
    );
    return [...new Set((encontrados || []).map((e) => e.toLowerCase()))];
  }, [pegado]);

  const suprimir = useCallback(async () => {
    setSuprimiendo(true);
    try {
      const res = await promotionsApi.campaignAction(
        accessToken,
        campaign._id,
        "suppress",
        { emails: emailsPegados },
      );
      const r = res?.data?.resumen;
      message.success(
        `${r?.marcados ?? 0} sacados del censo` +
          (r?.ignorados
            ? ` (${r.ignorados} ignorados: ya enviados o de otra lista)`
            : ""),
      );
      setModalSupresion(false);
      setPegado("");
      load();
    } catch (err) {
      message.error(err.message || "Error suprimiendo direcciones");
    } finally {
      setSuprimiendo(false);
    }
  }, [accessToken, campaign, emailsPegados, load]);

  const columns = [
    {
      title: "Persona",
      key: "persona",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          <Text copyable style={{ fontSize: 12, color: "#64748b" }}>
            {r.email}
          </Text>
        </div>
      ),
    },
    {
      title: "Cargo",
      dataIndex: "cargo",
      width: 160,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Origen",
      key: "origen",
      width: 170,
      render: (_, r) => {
        const t = TIPO_DOMINIO[r.tipo_dominio] || {
          color: "default",
          label: "-",
        };
        return (
          <div>
            <Tag color={t.color}>{t.label}</Tag>
            {r.personas_en_el_centro > 1 && (
              <Tooltip
                title={`En la lista habia ${r.personas_en_el_centro} personas de ${r.dominio}. Se escribe solo a esta.`}
              >
                <div style={{ fontSize: 11, color: "#2563eb" }}>
                  1 de {r.personas_en_el_centro} del centro
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Estado",
      dataIndex: "status",
      width: 130,
      render: (v, r) => {
        const e = ESTADO[v] || { color: "default", label: v };
        const motivo = r.error || r.skip_reason;
        return (
          <Tooltip title={motivo || ""}>
            <div>
              <Tag color={e.color}>{e.label}</Tag>
              {motivo && (
                <div
                  style={{
                    fontSize: 11,
                    color: "#94a3b8",
                    maxWidth: 120,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {motivo}
                </div>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Codigo",
      dataIndex: "promo_code",
      width: 160,
      render: (v) =>
        v ? (
          <Text
            copyable
            style={{ fontFamily: "Consolas, monospace", fontSize: 12 }}
          >
            {v}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Enviado",
      dataIndex: "sent_at",
      width: 120,
      render: (v) =>
        v ? dayjs(v).format("DD/MM HH:mm") : <Text type="secondary">—</Text>,
    },
  ];

  return (
    <Drawer
      title={
        <div>
          <div>{campaign?.name}</div>
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            Destinatarios de la campana
          </Text>
        </div>
      }
      width={1100}
      open={open}
      onClose={onClose}
      extra={
        <Space>
          <Tooltip title="Descarga lo que estas viendo. Filtra Pendientes antes para mandarlo a validar.">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={exportarCsv}
            >
              Exportar CSV
            </Button>
          </Tooltip>
          <Tooltip title="Pega aqui las direcciones que el validador ha marcado como malas">
            <Button
              size="small"
              icon={<UserDeleteOutlined />}
              onClick={() => setModalSupresion(true)}
            >
              Marcar invalidos
            </Button>
          </Tooltip>
          <Button size="small" icon={<ReloadOutlined />} onClick={load}>
            Refrescar
          </Button>
        </Space>
      }
    >
      <div
        style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}
      >
        <Segmented options={filtros} value={filtro} onChange={setFiltro} />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar por nombre, email o codigo"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ width: 300 }}
        />
      </div>

      <Table
        rowKey="_id"
        size="small"
        loading={loading}
        dataSource={visibles}
        columns={columns}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showTotal: (t) => `${t} destinatarios`,
        }}
      />

      <Modal
        title="Marcar direcciones como invalidas"
        open={modalSupresion}
        onCancel={() => setModalSupresion(false)}
        onOk={suprimir}
        okText={`Sacar ${emailsPegados.length} del censo`}
        cancelText="Cancelar"
        okButtonProps={{
          danger: true,
          disabled: emailsPegados.length === 0,
          loading: suprimiendo,
        }}
        width={640}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Solo afecta a los pendientes"
          description="A quien ya se le escribio no se le toca. Pega el resultado del validador tal cual: se queda con lo que tenga forma de email, da igual si es una columna, un CSV o una lista separada por comas."
        />
        <Input.TextArea
          rows={10}
          value={pegado}
          onChange={(e) => setPegado(e.target.value)}
          placeholder={"director@centro-uno.es\nsecretaria@centro-dos.es\n..."}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {emailsPegados.length} direcciones reconocidas.
        </Text>
      </Modal>
    </Drawer>
  );
}
